import { History as H } from 'history';
import { TFunction } from 'i18next';
import { parse, stringify } from 'qs';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { RouteComponentProps } from 'react-router';
import { SelectBase } from 'react-select';

import { CategoryOption, toJobBoard } from 'src/api/greenhouse/JobBoards';
import { OfficeJson } from 'src/api/greenhouse/contentTypes.greenhouse';
import { fetchOffices } from 'src/api/greenhouse/fetch';
import { generateId } from 'src/utils/IdGeneration';

type OnChangeHandler = Required<SelectBase<CategoryOption>['props']>['onChange'];

export function firstOrSingle<T>(maybeArray: T | ReadonlyArray<T> | undefined | null): T | undefined {
  if (maybeArray === null || maybeArray === undefined) return;
  return Array.isArray(maybeArray) ? maybeArray[0] : maybeArray;
}

export function toInt(param: unknown): number | undefined {
  if (param === null || param === undefined) return;

  const value = parseInt(Array.isArray(param) ? param[0] : param);

  if (typeof value !== 'number' || isNaN(value) || value < 0) return;

  return value;
}

export function getQueryParams(history: H) {
  const { departmentId, officeId } = parse(history.location.search, { ignoreQueryPrefix: true });

  return {
    departmentId: toInt(departmentId),
    officeId: toInt(officeId),
  };
}

export function tryDispatch<T>(dispatcher: React.Dispatch<React.SetStateAction<T>>, option: T | undefined) {
  if (option) dispatcher(option);
}

export function updateHistory(history: H, option: CategoryOption, paramName: string, vanityParam?: string) {
  const params: any = parse(history.location.search, { ignoreQueryPrefix: true });
  params[paramName] = option.value < 0 ? undefined : option.value;

  if (vanityParam) {
    params[vanityParam] = option.value < 0 ? undefined : generateId(option.label);
  }

  history.replace({ ...history.location, search: stringify(params) });
}

export function useGreenhouse(t: TFunction, history: RouteComponentProps['history']) {
  const ALL_DEPARTMENTS: CategoryOption = { value: -1, label: t('all-departments'), isParent: false, sortLabel: '', level: 0 };
  const ALL_OFFICES: CategoryOption = { value: -1, label: t('all-locations'), isParent: false, sortLabel: '', level: 0 };

  const [response, setResponse] = useState<OfficeJson[]>([]);
  const [department, setDepartment] = useState<CategoryOption>(ALL_DEPARTMENTS);
  const [office, setOffice] = useState<CategoryOption>(ALL_OFFICES);

  const [ optionsLoaded, setOptionsLoaded ] = useState(false);

  useEffect(() => {
    if (response.length > 0) return;

    fetchOffices().then(setResponse);
  });

  const jobBoard = useMemo(() => toJobBoard(response), [ response ]);
  const categories = useMemo(() => jobBoard.filter(department.value, office.value), [ department, office, jobBoard ]);

  const { departments, offices } = useMemo(() => ({
    departments: [ALL_DEPARTMENTS, ...jobBoard.departments],
    offices: [ALL_OFFICES, ...jobBoard.offices],
  }), [ jobBoard ]);

  const readQueryParams = useCallback(() => {
    if (departments.length <= 1 || offices.length <= 1) return false;

    const { departmentId, officeId } = getQueryParams(history);

    tryDispatch(setDepartment, departmentId ? departments.find(option => option.value === departmentId) : ALL_DEPARTMENTS);
    tryDispatch(setOffice, officeId ? offices.find(option => option.value === officeId) : ALL_OFFICES);

    return true;
  }, [ departments, offices, history ]);

  useEffect(() => setOptionsLoaded(readQueryParams()), [ departments, offices, history.location.search ]);

  useEffect(() => {
    window.addEventListener('popstate', readQueryParams);

    return () => window.removeEventListener('popstate', readQueryParams);
  }, [ readQueryParams ]);

  useEffect(() => {
    if (!optionsLoaded) return;
    updateHistory(history, department, t('department-id-query-param'), t('department-query-param'));
  }, [ optionsLoaded, department ]);

  useEffect(() => {
    if (!optionsLoaded) return;
    updateHistory(history, office, t('office-id-query-param'), t('office-query-param'));
  }, [ optionsLoaded, office ]);

  const selectDepartment = useCallback<OnChangeHandler>(value => tryDispatch(setDepartment, firstOrSingle(value)), [ setDepartment ]);

  const selectOffice = useCallback<OnChangeHandler>(value => tryDispatch(setOffice, firstOrSingle(value)), [ setOffice ]);

  return {
    optionsLoaded,
    categories,
    departments,
    offices,
    selectedDepartment: department,
    selectedOffice: office,
    selectDepartment,
    selectOffice,
  };
}
